export type {
  Guide,
  GuideCategory,
  GuideFAQ,
  GuideHowTo,
  GuideLocale,
  GuideLocaleContent,
  GuideSection,
} from './types';

import { guide as buyingPropertyMauritiusForeigners } from './buying-property-mauritius-foreigners';
import { guide as mauritiusPdsScheme } from './mauritius-pds-scheme';
import { guide as mauritiusTaxBenefits } from './mauritius-tax-benefits';
import { guide as movingToMauritius } from './moving-to-mauritius';
import { guide as firstTimeBuyerMauritius } from './first-time-buyer-mauritius';
import { guide as buyingPropertyProvence } from './buying-property-provence';
import { guide as franceBuyingCosts } from './france-buying-costs';
import { guide as mauritiusVsSouthAfrica } from './mauritius-vs-south-africa';
import { guide as bestAreasInvestMauritius } from './best-areas-invest-mauritius';
import { guide as mauritiusPropertyTypes } from './mauritius-property-types';
import { guide as beachfrontPropertyMauritius } from './beachfront-property-mauritius';
import { guide as luxuryVillaMauritius } from './luxury-villa-mauritius';
import { guide as retirementMauritius } from './retirement-mauritius';
import { guide as mauritiusResidencyPermits } from './mauritius-residency-permits';
import { guide as rentalIncomeMauritius } from './rental-income-mauritius';
import { guide as provenceLifestyleGuide } from './provence-lifestyle-guide';
import { guide as masProvenceBuying } from './mas-provence-buying';
import { guide as southAfricaToMauritius } from './south-africa-to-mauritius';
import { guide as offPlanPropertyMauritius } from './off-plan-property-mauritius';
import { guide as landPurchaseMauritius } from './land-purchase-mauritius';
import { guide as golfEstatePropertyMauritius } from './golf-estate-property-mauritius';
import { guide as marinaPropertyMauritius } from './marina-property-mauritius';
import { guide as penthouseMauritius } from './penthouse-mauritius';
import { guide as smartCityMauritius } from './smart-city-mauritius';
import { guide as ecoLuxuryPropertyMauritius } from './eco-luxury-property-mauritius';
import { guide as gatedCommunityMauritius } from './gated-community-mauritius';
import { guide as familyPropertyMauritius } from './family-property-mauritius';
import { guide as tropicalArchitectureMauritius } from './tropical-architecture-mauritius';
import { guide as staffQuartersPropertyMauritius } from './staff-quarters-property-mauritius';
import { guide as poolVillaMauritius } from './pool-villa-mauritius';
import { guide as investmentPortfolioMauritius } from './investment-portfolio-mauritius';
import { guide as newDevelopmentsMauritius } from './new-developments-mauritius';
import { guide as vineyardPropertyProvence } from './vineyard-property-provence';
import { guide as chateauSouthFrance } from './chateau-south-france';
import { guide as hilltopVillagePropertyProvence } from './hilltop-village-property-provence';
import { guide as modernVillaProvence } from './modern-villa-provence';
import { guide as seasonalLivingProvence } from './seasonal-living-provence';
import { guide as dualMarketInvestment } from './dual-market-investment';
import { guide as remoteWorkLuxuryProperty } from './remote-work-luxury-property';
import { guide as wellnessRetreatProperty } from './wellness-retreat-property';

import type { Guide, GuideCategory, GuideLocale, GuideLocaleContent } from './types';

export const BUYER_GUIDES: Guide[] = [
  buyingPropertyMauritiusForeigners,
  mauritiusPdsScheme,
  mauritiusTaxBenefits,
  movingToMauritius,
  firstTimeBuyerMauritius,
  buyingPropertyProvence,
  franceBuyingCosts,
  mauritiusVsSouthAfrica,
  bestAreasInvestMauritius,
  mauritiusPropertyTypes,
  beachfrontPropertyMauritius,
  luxuryVillaMauritius,
  retirementMauritius,
  mauritiusResidencyPermits,
  rentalIncomeMauritius,
  provenceLifestyleGuide,
  masProvenceBuying,
  southAfricaToMauritius,
  offPlanPropertyMauritius,
  landPurchaseMauritius,
  golfEstatePropertyMauritius,
  marinaPropertyMauritius,
  penthouseMauritius,
  smartCityMauritius,
  ecoLuxuryPropertyMauritius,
  gatedCommunityMauritius,
  familyPropertyMauritius,
  tropicalArchitectureMauritius,
  staffQuartersPropertyMauritius,
  poolVillaMauritius,
  modernVillaProvence,
  seasonalLivingProvence,
  dualMarketInvestment,
  remoteWorkLuxuryProperty,
  wellnessRetreatProperty,
  investmentPortfolioMauritius,
  newDevelopmentsMauritius,
  vineyardPropertyProvence,
  chateauSouthFrance,
  hilltopVillagePropertyProvence,
];

const GUIDE_LOCALES: GuideLocale[] = ['en', 'fr'];

const GUIDE_BY_ID = new Map(BUYER_GUIDES.map((guide) => [guide.id, guide]));

function resolveGuideLocale(locale: string): GuideLocale {
  return locale === 'fr' ? 'fr' : 'en';
}

/** Get locale-specific content for a guide, falling back to English. */
export function getGuideContent(guide: Guide, locale: string): GuideLocaleContent {
  const guideLocale = resolveGuideLocale(locale);
  return guide.locales[guideLocale] ?? guide.locales.en;
}

/** Find a guide by slug in any locale. */
export function getGuideBySlug(slug: string): Guide | undefined {
  return BUYER_GUIDES.find((guide) =>
    GUIDE_LOCALES.some((guideLocale) => guide.locales[guideLocale].slug === slug),
  );
}

/** All guides with locale-specific content for listing pages. */
export function getGuidesForLocale(
  locale: string,
): Array<Guide & { content: GuideLocaleContent }> {
  return BUYER_GUIDES.map((guide) => ({
    ...guide,
    content: getGuideContent(guide, locale),
  }));
}

/** Guides filtered by category for hub sections. */
export function getGuidesByCategory(
  locale: string,
  category: GuideCategory,
): Array<Guide & { content: GuideLocaleContent }> {
  return getGuidesForLocale(locale).filter((guide) => guide.category === category);
}

/** Related guides resolved from a guide's relatedGuideIds. */
export function getRelatedGuides(
  guide: Guide,
  locale: string,
): Array<Guide & { content: GuideLocaleContent }> {
  return guide.relatedGuideIds
    .map((id) => GUIDE_BY_ID.get(id))
    .filter((related): related is Guide => related !== undefined)
    .map((related) => ({
      ...related,
      content: getGuideContent(related, locale),
    }));
}

/** All slug params for static generation. */
export function getAllGuideSlugs(): string[] {
  const slugs = new Set<string>();
  for (const guide of BUYER_GUIDES) {
    for (const guideLocale of GUIDE_LOCALES) {
      slugs.add(guide.locales[guideLocale].slug);
    }
  }
  return Array.from(slugs);
}

/** Build hreflang alternates for a guide (locale-specific slugs). */
export function buildGuideHreflangLanguages(
  baseUrl: string,
  guide: Guide,
  supportedLocales: readonly string[],
): Record<string, string> {
  const languages: Record<string, string> = {};

  for (const locale of supportedLocales) {
    if (locale !== 'en' && locale !== 'fr') continue;
    const content = guide.locales[locale];
    languages[locale] = `${baseUrl}/${locale}/guides/${content.slug}`;
  }

  const defaultContent = guide.locales.en;
  languages['x-default'] = `${baseUrl}/en/guides/${defaultContent.slug}`;

  return languages;
}

/** Hub page metadata by locale. */
export function getGuidesHubMeta(locale: string): {
  title: string;
  description: string;
  keywords: string;
} {
  if (resolveGuideLocale(locale) === 'fr') {
    return {
      title: 'Guides Acheteurs | Investissement Immobilier à Maurice & en Provence',
      description:
        "Guides experts pour l'achat immobilier à l'Île Maurice et en Provence. Cadre juridique, avantages fiscaux, zones d'investissement et conseils pratiques.",
      keywords:
        'guide acheteur maurice, achat immobilier maurice, investir ile maurice, acheter provence, guide immobilier maurice',
    };
  }

  return {
    title: 'Buyer Guides | Property Investment in Mauritius & Provence',
    description:
      'Expert buyer guides for purchasing property in Mauritius and Provence. Legal requirements, tax benefits, area insights, and step-by-step buying advice.',
    keywords:
      'buyer guides mauritius, property buying guide, invest mauritius, buy property provence, mauritius real estate guide',
  };
}

export const GUIDE_CATEGORIES: GuideCategory[] = [
  'mauritius',
  'provence',
  'investment',
  'lifestyle',
];

export function getGuideCategoryLabel(category: GuideCategory, locale: string): string {
  const isFr = resolveGuideLocale(locale) === 'fr';
  const labels: Record<GuideCategory, { en: string; fr: string }> = {
    mauritius: { en: 'Mauritius', fr: 'Île Maurice' },
    provence: { en: 'Provence', fr: 'Provence' },
    investment: { en: 'Investment', fr: 'Investissement' },
    lifestyle: { en: 'Lifestyle', fr: 'Art de vivre' },
  };
  return isFr ? labels[category].fr : labels[category].en;
}
